// src/lib/calc.js — calculs métier compta (commissions, ménage, sous-loc, charges).
//
// Fonctions PURES : tous les inputs sont passés en argument, pas de closure
// sur cfg/data/month. Facilite tests unitaires futurs et permettra à terme de
// simuler des scénarios tarifaires (ex. tarification dynamique channel manager).
//
// Si un calcul nécessite l'objet `log` complet, le caller le récupère depuis
// cfg.props avant d'appeler. Évite de passer cfg systématiquement.

import { computeInvoiceMonthByNights, nightsInMonth } from './resa-invoice-month'
import { resoudreCommission } from './commission-taux-date'
import { lignesCommissionFacture, commissionTotaleFacture } from './commission-lignes'

// ── Mois de facturation d'une résa (règle "majorité de nuits") ──
// Détermine sur quel mois (clé "YYYY-MM") une résa doit être rattachée pour
// la facture mensuelle proprio.
//
// Règle métier FBZ (validée par Dimitri) :
//   - Mois où la résa a le PLUS DE NUITS
//   - En cas d'égalité parfaite : mois de check-in (le plus ancien)
//
// Convention nuits :
//   - La nuit du jour de check-in compte (= 1ère nuit dans le mois du check-in)
//   - Le jour de check-out NE compte PAS (le voyageur libère le logement)
//
// Exemples :
//   - 2026-03-28 → 2026-04-04 (7 nuits) :
//       Mars 4 (28, 29, 30, 31) | Avril 3 (1, 2, 3) → "2026-03"
//   - 2026-03-30 → 2026-04-03 (4 nuits, égalité 2-2) :
//       égalité → mois de check-in → "2026-03"
//   - 2026-04-02 → 2026-04-28 (26 nuits) :
//       trivialement → "2026-04"
//
// Scope (cf. ADR-049) : ce helper est utilisé UNIQUEMENT côté écriture
// (iCal / CSV / saisie manuelle) pour les nouvelles résas. Le data
// historique n'est pas migré (pas de facture émise via l'app jusqu'à
// présent, donc pas d'enjeu comptable rétroactif).
//
// @param {string} startDate "YYYY-MM-DD" check-in (obligatoire)
// @param {string} endDate   "YYYY-MM-DD" check-out (exclu du compte). Si
//                           absent, la résa est traitée comme 1 nuit
//                           commençant à startDate.
// @returns {string|null} "YYYY-MM" mois de facturation, null si startDate invalide
//
// Délègue au helper centralisé `computeInvoiceMonthByNights` (UTC pur,
// ADR-182) — corrige le bug DST de l'ancienne implémentation TZ-local
// (`setDate` sur Date locale). Signature externe inchangée (0-régression).
export function getResaInvoiceMonth(startDate, endDate) {
  return computeInvoiceMonthByNights(startDate, endDate)
}

// ── Taux de commission applicable à une résa selon durée séjour ──
// Mode dégressif : palier dont maxNuits ≥ nuits. Sinon taux fixe global du log.
export function getResaTaux(log, nuits) {
  if (!log || log.commissionMode !== 'degressive' || !log.paliers?.length) {
    return log?.taux ?? 0
  }
  const palier = [...log.paliers].sort((a, b) => a.maxNuits - b.maxNuits).find(p => (nuits || 0) <= p.maxNuits)
  return palier ? palier.taux : (log.tauxBase ?? log.taux ?? 0)
}

// ── IDs sous-loc (p6/Maison Zen) — pas de commission FBZ séparée ──
//
// Pour ces logements/chambres, le CA total_amount d'une résa direct interne
// est agrégé directement dans `calcSousLocForLog.ca` (cf. Q1 Dimitri ADR-106).
// FBZ n'applique pas de commission séparée : le bénéfice FBZ est
// (CA total − loyer − chargesVar), pas une % du CA.
const SOUSLOC_LIDS = new Set(['p6', 'lc1', 'lc2', 'lc3'])

export function isSousLocLid(lid) {
  return SOUSLOC_LIDS.has(lid)
}

// ── Calcul central : ca / commission / ménage / net / extras pour un log/mois ──
//
// Signature pure (vs ancienne version dans App qui fermait sur cfg/data/month).
// Retour identique à l'ancien : { ca, men, taxe, nuitee, comm, total, nuits,
// sejours, nbResa, net, caAirbnb, caBooking, extrasTotal, resas, extras }.
//
// `taux` reste un argument explicite (souvent log.taux mais peut être 0 pour
// sous-loc, ou un autre taux simulé). En mode dégressif, `taux` est ignoré et
// la commission est recalculée par résa via getResaTaux(logDef).
export function calc(cfg, data, lid, taux, mk) {
  const d = data?.[mk]?.[lid] || {}
  const resas = (d.reservations || []).filter(Boolean)
  const ca = resas.reduce((s, r) => s + (r.montant || 0), 0)
  const men = resas.reduce((s, r) => s + (r.menage || 0), 0)
  const taxe = resas.reduce((s, r) => s + (r.taxe || 0), 0)
  const nuits = resas.reduce((s, r) => s + (r.nuits || 0), 0)
  // Base de calcul commission FBZ = montant reçu − ménage. Universelle
  // toutes sources (Airbnb / Booking / Direct / Vrbo). La taxe de séjour
  // (`r.taxe`, surtout présente sur Booking où elle transite par le compte
  // proprio avant reversement mairie) reste trackée séparément dans `c.taxe`
  // mais NE doit PAS réduire la base commission. Cf. ADR-048.
  const nuitee = Math.max(0, ca - men)
  const logDef = cfg.props.flatMap(p => p.logs).find(l => l.id === lid)
  // ── Taux DATÉ (lot taux-date) ──
  // `bareme` = le barème de commission EN VIGUEUR au mois `mk`. Tant que le
  // logement n'a pas de `tauxHistorique` — cas de TOUT le parc au jour de
  // l'écriture — `historise` est false et les deux lignes suivantes retombent
  // LITTÉRALEMENT sur le code d'avant : `logDef` pour le dégressif, l'argument
  // `taux` pour le fixe. Un mois passé garde ainsi son taux d'époque sans que
  // rien ne change pour un logement non historisé.
  const bareme = resoudreCommission(logDef, mk)
  const baremeSrc = bareme.historise ? bareme : logDef
  const tauxFixe = bareme.historise ? bareme.taux : taux
  // ── UNE seule formule de commission (2026-09-30) ──
  // Commission de CHAQUE réservation arrondie au centime, puis additionnée :
  // exactement la facture (src/lib/commission-lignes.js). Avant, calc()
  // appliquait le taux au TOTAL des bases → 1 centime d'écart avec le PDF
  // (Le Havre de Bruguières, sept. 2026 : 1 083,14 € écran, 1 083,15 € PDF),
  // répercuté sur Facturation, Annuel, Compta, Statistiques, rapport, opérateur.
  // Un taux manquant donne toujours NaN, traité juste en dessous.
  let comm = commissionTotaleFacture(lignesCommissionFacture(resas, { baremeSrc, tauxFixe }))
  // ── La commission muette (paquet argent 2026-09-03) ──
  // `taux` manquant (undefined/NaN) en mode non-dégressif → comm = NaN, que
  // mergeCalc écrasait en 0 : la commission était silencieusement OMISE du
  // total. Règle : s'il y a une base à commissionner (nuitee > 0), le NaN est
  // CONSERVÉ (le total s'affiche « — », jamais un total amputé) et SIGNALÉ via
  // `commManquante`. Sans base (mois vide), la commission est un vrai 0.
  // Un taux 0 explicite (sous-loc) reste un vrai 0 : Number.isFinite(0) → pas touché.
  let commManquante = false
  if (!Number.isFinite(comm)) {
    if (nuitee > 0) commManquante = true
    else comm = 0
  }
  const caAirbnb = resas.filter(r => r.plateforme === "airbnb").reduce((s, r) => s + (r.montant || 0), 0)
  const caBooking = resas.filter(r => r.plateforme === "booking").reduce((s, r) => s + (r.montant || 0), 0)
  const extrasTotal = (d.extras || []).reduce((s, e) => s + (e.montant || 0), 0)
  // ── Option B (occupation en « nuits réelles par mois ») — ADDITIF ──
  // nuitsOccup = nuits du périmètre (ce lid) RÉELLEMENT occupées en `mk`,
  // toutes résas confondues (y compris une résa à cheval logée dans un AUTRE
  // bucket). On scanne TOUS les mois de `data` (skip namespaces `_*`) et somme
  // nightsInMonth(r, mk). `nuits` (TOTAL séjour) reste INCHANGÉ (chemin argent
  // intouché : commission lit getResaTaux(logDef, r.nuits) = total).
  const nuitsOccup = nightsInMonthsForLid(data, lid, mk)
  return {
    ca, men, taxe, nuitee, comm,
    commManquante,
    total: comm + men,
    nuits,
    nuitsOccup,
    sejours: resas.length,
    nbResa: resas.length,
    net: ca - comm - men,
    caAirbnb, caBooking, extrasTotal,
    resas,
    extras: d.extras || [],
  }
}

// ── nightsInMonthsForLid(data, lid, mk) : Σ nuits réelles occupées en mk ──
// Scanne TOUS les buckets mois de `data` (skip clés non-mois / namespaces `_*`)
// pour le `lid` donné et somme nightsInMonth(r, mk). Une résa à cheval ne vit
// que dans UN bucket (son mois majoritaire) ; pour compter ses nuits dans `mk`
// il faut la trouver où qu'elle soit → scan complet. Plafond non appliqué ici
// (1 logement = pas de double-booking attendu côté admin ; le moteur
// stats-aggregate borne par unité). Pure, défensive (data absent → 0).
export function nightsInMonthsForLid(data, lid, mk) {
  if (!data || !lid || !mk) return 0
  let s = 0
  // ── INVARIANCE OCCUPATION vs PRORATA (Lot 5a) ──
  // Le prorata (5e calque) fait apparaître la MÊME résa (dates pleines) dans N
  // buckets avec un montant partiel chacun — mais l'occupation (nuits réelles)
  // NE DOIT PAS suivre le découpage d'argent. On DÉDUP les sous-lignes virtuelles
  // (porteuses du marqueur `_prorataOf`) par identité de code stable : la 1ʳᵉ
  // occurrence compte ses nuits, les suivantes sont skippées.
  // INERTIE byte-identique : sur des données SANS `_prorataOf` (prod actuelle),
  // aucune résa ne porte ce marqueur → le skip ne se déclenche JAMAIS → somme
  // strictement identique à avant (une résa normale vit dans 1 seul bucket).
  const seen = new Set()
  for (const key of Object.keys(data)) {
    if (!/^\d{4}-\d{2}$/.test(key)) continue // skip `_*` et clés non-mois
    const resas = data[key] && data[key][lid] && data[key][lid].reservations
    if (!Array.isArray(resas)) continue
    for (const r of resas) {
      if (!r) continue
      if (r._prorataOf) {
        const id =
          r._prorataOf || r.airbnbCode || r.bookingRef || r.id || null
        if (id != null && seen.has(id)) continue // sous-ligne déjà comptée
        if (id != null) seen.add(id)
      }
      s += nightsInMonth(r, mk)
    }
  }
  return s
}

// ── Agrégation résas direct interne (pricing_blocks) — ADR-106 / PR #169b ──
//
// `pricing_blocks` (Supabase V2 relationnel) porte deux usages depuis ADR-104 :
//   1. Blocage simple (guest_first_name NULL, total_amount NULL) → exclu ici.
//   2. Résa direct interne FBZ (guest_first_name NOT NULL + total_amount > 0).
//
// Ce helper agrège uniquement le cas (2). Le loader hook
// `usePricingBlocksHistory` (src/lib/use-pricing-blocks-history.js) filtre
// déjà côté serveur via `.not('guest_first_name', 'is', null)` et applique
// la règle ADR-049 "majorité de nuits" (getResaInvoiceMonth) pour qu'un bloc
// multi-mois soit comptabilisé dans UN SEUL monthkey (anti-double-comptage H-1).
//
// Conventions Q1 Dimitri (cf. ADR-106) :
//   - Logement p1-p5 (conciergerie classique) : commission = ca × taux / 100
//     (résa direct interne = FBZ a trouvé le voyageur sans OTA, mais le
//      proprio touche tout de même son net comme une résa OTA direct).
//   - Logement p6/lc1/lc2/lc3 (sous-loc Maison Zen) : commission = 0,
//     car le CA est intégralement bénéfice FBZ (pas de proprio à payer).
//     Le CA est par ailleurs agrégé via calcSousLocForLog (cf. signature
//     étendue + appelants) pour apparaître dans la vue Sous-loc.
//
// `blocksByMonth` shape : Map<monthKey "YYYY-MM", Map<logement_id, Array<block>>>
// (loader hook fournit la même Map à travers tous les appelants).
//
// @param {object} blocksByMonth — Map indexée mois × logement (peut être null/undefined)
// @param {string} lid — logement_id
// @param {number} taux — taux commission % (ignoré si lid est sous-loc)
// @param {string} mk — monthkey "YYYY-MM"
// @returns {{ca, men, taxe, nuitee, comm, total, nuits, sejours, nbResa, net, caAirbnb, caBooking, extrasTotal, resas, extras}}
//   Mêmes clés que calc() pour permettre fusion via mergeCalc(). Champs non
//   pertinents pour direct interne (men, taxe, caAirbnb, caBooking, extras*)
//   sont à 0 / [].
export function calcDirectInternePure(blocksByMonth, lid, taux, mk) {
  const empty = {
    ca: 0, men: 0, taxe: 0, nuitee: 0, comm: 0, total: 0, nuits: 0,
    sejours: 0, nbResa: 0, net: 0, caAirbnb: 0, caBooking: 0,
    extrasTotal: 0, resas: [], extras: [],
  }
  if (!blocksByMonth || !mk || !lid) return empty
  const monthMap = blocksByMonth.get ? blocksByMonth.get(mk) : null
  if (!monthMap) return empty
  const blocks = monthMap.get ? monthMap.get(lid) : null
  if (!blocks || blocks.length === 0) return empty

  const isSousLoc = isSousLocLid(lid)
  let ca = 0
  let nuits = 0
  // Taxe de séjour FIGÉE à la résa directe (lot « Taxe directe », Voie A).
  // `b.taxe_sejour` est enrichi en amont par le loader (use-pricing-blocks-history
  // côté admin / buildDirectBlocksByLidMk côté proprio) via un 2e SELECT sur
  // direct_booking_requests.tarif_snapshot_breakdown->>'taxe_sejour' (pas de FK →
  // pas de jointure embarquée possible). Bloc manuel admin (sans request) → absente
  // → 0. Montant SÉPARÉ à reverser : JAMAIS ajouté au CA (cf. return ci-dessous).
  let taxeSej = 0
  const resas = []
  for (const b of blocks) {
    const amount = Number(b.total_amount) || 0
    ca += amount
    const taxeBloc = Number(b.taxe_sejour) || 0
    taxeSej += taxeBloc
    // Nuits = (end_date - start_date) jours. start/end ISO YYYY-MM-DD.
    if (b.start_date && b.end_date) {
      const s = new Date(b.start_date + 'T00:00:00')
      const e = new Date(b.end_date + 'T00:00:00')
      if (!isNaN(s.getTime()) && !isNaN(e.getTime()) && e > s) {
        nuits += Math.round((e - s) / 86400000)
      }
    }
    // Pseudo-reservation pour API uniforme avec resas KV V1
    // (consommé par genRapport listing détaillé PDF Option A Q3 Dimitri).
    resas.push({
      id: b.id,
      logement_id: b.logement_id,
      voyageur: [b.guest_first_name, b.guest_last_name].filter(Boolean).join(' ') || '—',
      guest_first_name: b.guest_first_name,
      guest_last_name: b.guest_last_name,
      plateforme: 'direct',
      source: 'pricing_block_internal',
      checkIn: b.start_date,
      start_date: b.start_date,
      end_date: b.end_date,
      montant: amount,
      menage: 0,
      taxe: taxeBloc,
      nuits: b.start_date && b.end_date
        ? Math.max(0, Math.round((new Date(b.end_date + 'T00:00:00') - new Date(b.start_date + 'T00:00:00')) / 86400000))
        : 0,
    })
  }
  // Commission FBZ : 0 pour sous-loc (CA pur agrégé via calcSousLocForLog),
  // ca * taux / 100 sinon (conciergerie classique p1-p5).
  const comm = isSousLoc ? 0 : (ca * (taux || 0) / 100)
  return {
    // taxe = SOMME des taxes de séjour figées des blocs (montant à reverser,
    // SÉPARÉ du CA). round2 inline (pas de round2 importé dans ce module).
    ca, men: 0, taxe: Math.round(taxeSej * 100) / 100, nuitee: ca, comm,
    total: comm + 0,
    nuits,
    sejours: blocks.length,
    nbResa: blocks.length,
    net: ca - comm,
    caAirbnb: 0, caBooking: 0, extrasTotal: 0,
    resas,
    extras: [],
  }
}

// ── Fusion deux résultats calc-like (KV résas + pricing_blocks direct) ──
//
// Préserve les champs scalaires de `base` (men, taxe, caAirbnb, caBooking,
// extrasTotal — direct interne n'en a pas par construction) et somme les
// champs cumulables. Concat les arrays `resas` (PDF listing détaillé).
// IMPORTANT (L-2 pessimist) : extrasTotal vient toujours de base, jamais de direct.
export function mergeCalc(base, direct) {
  if (!base) return direct || null
  if (!direct) return base
  // ── La commission muette (paquet argent 2026-09-03) ──
  // `|| 0` écrasait un comm/total/net NaN (taux manquant, signalé par calc())
  // en 0 : le total fusionné était silencieusement amputé de la commission.
  // Somme NaN-propagante pour CES TROIS champs : null/undefined restent 0
  // (comportement d'origine), mais un NaN signalé RESTE NaN (affiché « — »).
  const addNan = (a, b) => (Number.isNaN(a) || Number.isNaN(b)) ? NaN : (a || 0) + (b || 0)
  return {
    ca: (base.ca || 0) + (direct.ca || 0),
    men: base.men || 0,
    taxe: base.taxe || 0,
    nuitee: (base.nuitee || 0) + (direct.nuitee || 0),
    comm: addNan(base.comm, direct.comm),
    commManquante: !!(base.commManquante || direct.commManquante),
    total: addNan(base.total, direct.total),
    nuits: (base.nuits || 0) + (direct.nuits || 0),
    // Option B : occupation réelle. Le direct interne (blocs intra-mois) occupe
    // aussi → on ajoute direct.nuits (calcDirectInternePure n'a pas de nuitsOccup
    // séparé ; ses blocs sont mono-mois par construction du hook).
    nuitsOccup: (base.nuitsOccup || 0) + (direct.nuits || 0),
    sejours: (base.sejours || 0) + (direct.sejours || 0),
    nbResa: (base.nbResa || 0) + (direct.nbResa || 0),
    net: addNan(base.net, direct.net),
    caAirbnb: base.caAirbnb || 0,
    caBooking: base.caBooking || 0,
    extrasTotal: base.extrasTotal || 0,
    resas: [...(base.resas || []), ...(direct.resas || [])],
    extras: base.extras || [],
  }
}

// ── Marge ménage par logement ──
// Si le log a un forfait `menRealCostPerClean` configuré : marge = collecté − coutReel.
// Sinon : marge=null, margeActive=false (l'UI n'affiche pas la ligne marge).
export function calcMenage(log, month, data) {
  const resas = (data?.[month]?.[log.id]?.reservations || []).filter(Boolean)
  const collecte = resas.reduce((s, r) => s + (r.menage || 0), 0)
  // BUG 7 (règle A décidée) : 1 ménage = 1 vrai check-out = 1 résa avec CA > 0,
  // quel que soit le montant ménage collecté. Les résas montant=0 (blocs) ne
  // génèrent pas d'intervention. Avant : on comptait les résas ménage>0 (faux
  // négatif quand le ménage était facturé 0 mais l'intervention bien réelle).
  const nbInterventions = resas.filter(r => (r.montant || 0) > 0).length
  const forfait = Number(log.menRealCostPerClean) || 0
  const margeActive = forfait > 0
  const coutReel = margeActive ? nbInterventions * forfait : null
  const marge = margeActive ? collecte - coutReel : null
  return { collecte, nbInterventions, coutReel, marge, margeActive }
}

// ── Résumé financier sous-loc pour un log parent ──
// Source de vérité unique (utilisé par calcBenefSousLoc et ComptaTab).
// CA = Σ résas des chambres (résas stockées sur ch.id, pas sur log.id — piège
// historique documenté dans App.jsx L6325-6326). Charges = loyer + chargesVar du log parent.
//
// PR #169b (ADR-106) : `blocksByMonth` optionnel. Si fourni, ajoute le
// `total_amount` des résas direct internes sur chaque chambre au CA agrégé.
// Signature backward-compatible : si `blocksByMonth` absent → comportement
// historique inchangé (juste les résas KV V1).
export function calcSousLocForLog(log, month, data, blocksByMonth) {
  const ca = (log.chambres || []).reduce((s, ch) => {
    const caKv = (data?.[month]?.[ch.id]?.reservations || []).filter(Boolean).reduce((ss, r) => ss + (r.montant || 0), 0)
    // PR #169b : agréger aussi les pricing_blocks direct internes de la chambre.
    let caBlocks = 0
    if (blocksByMonth) {
      const monthMap = blocksByMonth.get ? blocksByMonth.get(month) : null
      const chBlocks = monthMap?.get ? monthMap.get(ch.id) : null
      if (chBlocks) {
        caBlocks = chBlocks.reduce((ss, b) => ss + (Number(b.total_amount) || 0), 0)
      }
    }
    return s + caKv + caBlocks
  }, 0)
  const slData = data?.[month]?.[log.id] || {}
  const loyer = (slData.loyer || []).reduce((s, x) => s + (x.montant || 0), 0)
  const chargesVar = (slData.chargesVar || []).reduce((s, x) => s + (x.montant || 0), 0)
  return { ca, loyer, chargesVar, net: ca - loyer - chargesVar }
}

// ── Allocation charges sous-loc par chambre (BUG 4, clé 1/N parts égales) ──
// Répartit le loyer et les charges variables du log parent entre ses N chambres
// à parts égales (décision Dimitri : 1/N). Pure : tous les inputs en argument.
//
// Retour : [{ chambreId, caChambre, loyerAlloue, chargesAllouees, beneficeChambre }]
//   - caChambre      = Σ montant des résas KV de la chambre (data[month][ch.id])
//                      + Σ total_amount des blocs direct internes de la chambre
//                      si `blocksByMonth` est fourni (même règle que
//                      calcSousLocForLog — lot Statistiques 2026-09)
//   - loyerAlloue    = loyerParent / N
//   - chargesAllouees= chargesVarParent / N
//   - beneficeChambre= caChambre - loyerAlloue - chargesAllouees
//
// INVARIANT (testé) : Σ beneficeChambre === calcSousLocForLog(parentLog, month,
// data, blocksByMonth).net (à epsilon d'arrondi), les DEUX appelés avec le même
// blocksByMonth (ou aucun). Signature backward-compatible : `blocksByMonth`
// absent → comportement historique inchangé (résas KV seules).
// Garde division-par-zéro : N=0 → [] (loyer/charges non répartis, cohérent
// avec l'absence de chambre support).
export function allocSousLocCharges(parentLog, month, data, blocksByMonth) {
  // Dédup par id (anti-bug "x3" historique ADR-051) : si cfg référence une
  // chambre en double, on ne compte qu'une part (sinon N gonflé → loyer sous-réparti).
  const seen = new Set()
  const chambres = (parentLog?.chambres || []).filter(ch => {
    if (!ch || seen.has(ch.id)) return false
    seen.add(ch.id)
    return true
  })
  const n = chambres.length
  if (n === 0) return []
  const slData = data?.[month]?.[parentLog.id] || {}
  const loyer = (slData.loyer || []).reduce((s, x) => s + (x.montant || 0), 0)
  const chargesVar = (slData.chargesVar || []).reduce((s, x) => s + (x.montant || 0), 0)
  const loyerAlloue = loyer / n
  const chargesAllouees = chargesVar / n
  return chambres.map(ch => {
    // Garde main (paquet gardes) : .filter(Boolean) contre les entrées nulles.
    const caKv = (data?.[month]?.[ch.id]?.reservations || []).filter(Boolean)
      .reduce((ss, r) => ss + (r.montant || 0), 0)
    // Lot Statistiques : agréger aussi les pricing_blocks direct internes de la
    // chambre (miroir exact de calcSousLocForLog — même invariant Σ = net).
    let caBlocks = 0
    if (blocksByMonth) {
      const monthMap = blocksByMonth.get ? blocksByMonth.get(month) : null
      const chBlocks = monthMap?.get ? monthMap.get(ch.id) : null
      if (chBlocks) {
        caBlocks = chBlocks.reduce((ss, b) => ss + (Number(b.total_amount) || 0), 0)
      }
    }
    const caChambre = caKv + caBlocks
    return {
      chambreId: ch.id,
      caChambre,
      loyerAlloue,
      chargesAllouees,
      beneficeChambre: caChambre - loyerAlloue - chargesAllouees,
    }
  })
}

// ── Bénéfice sous-location agrégé ──
// Σ des bénéfices nets de tous les logs parents en p6. Peut être négatif.
// PR #169b : `blocksByMonth` propagé pour inclure résas direct internes.
export const calcBenefSousLoc = (cfg, month, data, blocksByMonth) =>
  cfg.props.filter(p => p.id === "p6").reduce((s, p) =>
    s + p.logs.reduce((ss, l) => ss + calcSousLocForLog(l, month, data, blocksByMonth).net, 0), 0)

// ── Total des charges fixes récurrentes FBZ ──
// Charges structurelles non mensualisées (abos, comptable, etc.) — blob fbz8-compta.
// Utilisé par le bandeau KPI Accueil pour calculer la marge nette FBZ.
export const calcChargesFixes = (compta) =>
  (compta?.chargesFixes || []).reduce((s, c) => s + (c.montant || 0), 0)

// ── Données sous-loc (loyer + chargesVar) avec defaults pour init UI ──
// Retourne la structure persistée dans data[month][lid] ou les defaults.
export function getSousLocData(data, month, lid) {
  const d = data?.[month]?.[lid] || {}
  return {
    loyer: d.loyer || [
      { id: "sl1", label: "Loyer nu", montant: 0 },
      { id: "sl2", label: "Électricité", montant: 0 },
      { id: "sl3", label: "Gaz", montant: 0 },
      { id: "sl4", label: "Poubelles", montant: 0 },
    ],
    chargesVar: d.chargesVar || [],
  }
}
